import React, { memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { CreateCustomerButton } from 'src/components/customer/create-customer/CreateCustomerButton'
import { CustomersTable } from 'src/components/customer/customers-table/CustomersTable'
import { CustomersTableToolbar } from 'src/components/customer/customers-table/CustomersTableToolbar'
import { CustomersTabs } from 'src/components/customer/customers-tabs/CustomersTabs'
import { AdminFetchCustomers } from 'src/constants/actionTypes'
import { CustomerStatus } from 'src/constants/customer/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'

/**
 * Laid out like the dashboard's list pages (Contractors, Opportunities): an h2 with the page CTA
 * on the right, then the table card — status tabs, filter row, grid — as one white block that
 * fills the rest of the viewport.
 */
const CustomersPageComponent = () => {
  // Notion documents Status = Active as the default; the API has no status param, so the tabs
  // narrow the already-loaded list in memory.
  const [selectedStatus, setSelectedStatus] = useState<CustomerStatus>(CustomerStatus.Active)
  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button and by a successful create — included in the fetch effect's
  // dependency array below so either one re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)

  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  // POST /partners is `partners_create` (ENG-1207 part 2) — the CTA is hidden, not disabled.
  const canCreateCustomer = useHasPermission(InternalPermission.PartnersCreate)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect for the customer list: fetches every customer (status, search and
  // sorting are all client-side), so it only reruns on auth changes or a manual refresh — not on
  // pagination, the tab or the search term.
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomers.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken])

  return (
    // A flex column so the grid takes exactly the height left under the header, tabs and filter
    // row. `min-h-0` lets the page shrink to its column, and `overflow-hidden` means the grid
    // body is the only thing that scrolls — never the page behind it as well.
    <div
      data-testid="admin-customers-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <div css={tw`mb-6 flex shrink-0 flex-row items-center justify-between gap-4`}>
        <Typography variant="h2">Customer list</Typography>
        {canCreateCustomer ? <CreateCustomerButton onCreated={handleRefresh} /> : null}
      </div>
      <div css={tw`shrink-0`}>
        <CustomersTabs selectedStatus={selectedStatus} onChange={setSelectedStatus} />
        <CustomersTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
      </div>
      <CustomersTable status={selectedStatus} searchQuery={searchQuery} />
    </div>
  )
}

export const CustomersPage = memo(CustomersPageComponent)
